import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import Logo from "./Logo";
import { anchorScroll } from "./scroll";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Loan Review", href: "#loan-review" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

const NavLink = ({ l, onGo, className = "" }) => {
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  if (l.href.startsWith("#")) {
    return isHome ? (
      <a
        href={l.href}
        data-testid={`nav-link-${l.href.slice(1)}`}
        onClick={(e) => {
          anchorScroll(e, l.href.slice(1));
          onGo && onGo();
        }}
        className={className}
      >
        {l.label}
      </a>
    ) : (
      <a
        href={`/${l.href}`}
        data-testid={`nav-link-${l.href.slice(1)}`}
        onClick={onGo}
        className={className}
      >
        {l.label}
      </a>
    );
  }
  return (
    <a
      href={l.href}
      data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
      onClick={onGo}
      className={className}
    >
      {l.label}
    </a>
  );
};

const desktopCls =
  "link-sweep font-mono text-[11px] uppercase tracking-[0.18em] text-ink/65 transition-colors hover:text-eucalyptus whitespace-nowrap";
const mobileCls =
  "rounded-lg px-3 py-3 font-display text-2xl text-ink transition-colors hover:bg-mint hover:text-eucalyptus";

const Nav = () => {
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <motion.div
        initial={{ y: -70, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        className="backdrop-blur-xl bg-ivory/85 border-b border-ink/10"
      >
        <nav className="mx-auto flex h-[76px] max-w-[92rem] items-center justify-between gap-4 px-6 lg:px-12">
          <Logo />

          <div className="hidden items-center gap-5 lg:flex xl:gap-7">
            {LINKS.map((l) => (
              <NavLink key={l.label} l={l} className={desktopCls} />
            ))}
            <Link
              to="/intelligence"
              data-testid="nav-link-intelligence"
              className="link-sweep font-mono text-[11px] tracking-[0.18em] transition-colors hover:text-eucalyptus text-ink/65 whitespace-nowrap"
            >
              iNtelligence
            </Link>
            <a
              href="tel:0414345736"
              data-testid="nav-call-button"
              className="group inline-flex items-center gap-2.5 whitespace-nowrap rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-ivory transition-all duration-300 hover:-translate-y-0.5 hover:bg-eucalyptus hover:text-ivory"
            >
              <Phone className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
              0414 345 736
            </a>
          </div>

          <button
            className="text-ink lg:hidden"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </nav>

        <AnimatePresence>
          {open && (
            <motion.div
              data-testid="mobile-menu-panel"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden border-t border-ink/10 bg-ivory/95 backdrop-blur-xl lg:hidden"
            >
              <div className="flex flex-col gap-1 px-6 py-6">
                {LINKS.map((l) => (
                  <NavLink
                    key={l.label}
                    l={l}
                    onGo={() => setOpen(false)}
                    className={mobileCls}
                  />
                ))}
                <Link
                  to="/intelligence"
                  data-testid="mobile-link-intelligence"
                  onClick={() => setOpen(false)}
                  className={mobileCls}
                >
                  iNtelligence
                </Link>
                <a
                  href="tel:0414345736"
                  data-testid="mobile-call-button"
                  onClick={() => setOpen(false)}
                  className="mt-3 inline-flex items-center justify-center gap-2.5 rounded-full bg-ink px-5 py-3.5 text-sm font-semibold text-ivory"
                >
                  <Phone className="h-4 w-4" />
                  See how my loan stacks up
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </header>
  );
};

export default Nav;
